<script setup lang="ts">
import { computed } from 'vue'
import CollapsibleCard from './CollapsibleCard.vue'
import { fmtDayMon } from '../japan/japanReasons'

const props = defineProps<{
  // 'published' (Japan fund page UI Task 12): no synth NAV — the card shows
  // the latest published fund NAV in pence plus how stale it is.
  source: 'kt' | 'aggregate' | 'published' | null
  ktReturnPct: number | null
  basketReturnPct: number | null
  anchorPence: number | null
  anchorDateLabel: string | null
  publishedDate?: string | null
  publishedNavPence?: number | null
  staleDays?: number | null
}>()

// --- 'published' variant ---
const hasPublished = computed(() =>
  props.publishedDate != null && props.publishedNavPence != null && Number.isFinite(props.publishedNavPence))
const publishedNavLabel = computed(() =>
  hasPublished.value ? `${(props.publishedNavPence as number).toFixed(2)}p` : '')
const publishedDateLabel = computed(() => props.publishedDate ? fmtDayMon(props.publishedDate) : '')
const staleLabel = computed(() => {
  if (props.staleDays == null) return ''
  if (props.staleDays <= 0) return 'current'
  return `${props.staleDays} business day${props.staleDays === 1 ? '' : 's'} old`
})
// The latest NAV is normally 1 business day old at the 08:40 decision (the
// fund strikes the previous day's close); 2+ means a missed publish.
const publishedStale = computed(() => hasPublished.value && props.staleDays != null && props.staleDays >= 2)
const publishedSummary = computed(() => {
  if (!hasPublished.value) return 'No published NAV yet'
  const stale = staleLabel.value ? ` · ${staleLabel.value}` : ''
  return `Published ${publishedNavLabel.value} (${publishedDateLabel.value})${stale}`
})

const sourceLabel = computed(() => props.source === 'kt' ? 'K_t' : props.source === 'aggregate' ? 'Aggregate' : '--')

const deltaPct = computed(() => {
  if (props.source !== 'aggregate' || props.ktReturnPct == null || props.basketReturnPct == null) return null
  return props.ktReturnPct - props.basketReturnPct
})

const summary = computed(() => {
  if (props.ktReturnPct == null) return `${sourceLabel.value} · K_t input not fresh — falling back to legacy`
  const ktStr = (props.ktReturnPct >= 0 ? '+' : '') + props.ktReturnPct.toFixed(2) + '%'
  const delta = deltaPct.value != null ? ` (Δ ${(deltaPct.value >= 0 ? '+' : '') + deltaPct.value.toFixed(2)}%)` : ''
  return `${sourceLabel.value} · K_t ${ktStr}${delta}`
})
</script>

<template>
  <div v-if="source === 'published'" data-testid="nav-published">
    <CollapsibleCard title="NAV Source" testid="nav-source-card" :summary="publishedSummary" :alert="publishedStale">
      <div v-if="hasPublished" class="synth-source-strip">
        <span class="synth-source-pill published-pill">Published NAV</span>
        <span class="synth-source-detail">{{ publishedNavLabel }} ({{ publishedDateLabel }})</span>
      </div>
      <div v-else class="synth-source-strip">
        <span class="synth-source-detail"><em>No published NAV yet</em></span>
      </div>
      <div v-if="hasPublished && staleLabel" class="synth-source-anchor" :class="{ 'published-stale': publishedStale }">
        {{ staleLabel }}
      </div>
    </CollapsibleCard>
  </div>
  <div v-else-if="source" data-testid="kt-decomposition">
    <!-- Wrapper above carries the legacy `kt-decomposition` testid so it
         stays reachable from the top while the CollapsibleCard body is
         closed. The testid lives ONLY there — a second copy on the inner
         strip would trip Playwright strict-mode locators (2 matches). -->
    <CollapsibleCard title="NAV Source" testid="nav-source-card" :summary="summary">
      <!-- K_t vs aggregate source-of-truth strip — BGA-only by construction
           (source is null on JH, handled by the v-if above the whole card). -->
      <div class="synth-source-strip"
           :class="{ 'synth-source-kt': source === 'kt', 'synth-source-agg': source === 'aggregate' }">
        <span class="synth-source-pill">{{ sourceLabel }}</span>
        <span class="synth-source-detail" v-if="ktReturnPct != null">
          K_t = {{ (ktReturnPct >= 0 ? '+' : '') + ktReturnPct.toFixed(2) }}%
          <template v-if="deltaPct != null">
            (Δ {{ (deltaPct >= 0 ? '+' : '') + deltaPct.toFixed(2) }}%)
          </template>
        </span>
        <span class="synth-source-detail" v-else-if="source === 'kt'">
          <em>K_t input not fresh — falling back to legacy</em>
        </span>
      </div>

      <!-- Muted anchor-provenance line — which official NAV the synth figure
           above is anchored to, and when it last rolled forward. -->
      <div class="synth-source-anchor" v-if="anchorPence != null || anchorDateLabel">
        anchor {{ anchorPence != null ? Math.round(anchorPence) + 'p' : '--' }}{{ anchorDateLabel ? ' (' + anchorDateLabel + ')' : '' }}
      </div>
    </CollapsibleCard>
  </div>
</template>

<style scoped>
/* 2026-05-17: synth-NAV source-of-truth indicator. Pill on the left shows
   the active source (K_t vs Aggregate); detail on the right shows the
   K_t-derived return for side-by-side comparison. Subtle styling — this
   is operator diagnostic info, not a primary visual element. */
.synth-source-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: #8b949e;
}
.synth-source-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.4px;
}
.synth-source-kt .synth-source-pill {
  background: #1f3a52;
  color: #79c0ff;
  border: 1px solid #2f81f7;
}
.synth-source-agg .synth-source-pill {
  background: #2d2d2d;
  color: #adbac7;
  border: 1px solid #444c56;
}
.synth-source-detail { font-variant-numeric: tabular-nums; }
.synth-source-detail em { color: #d29922; font-style: normal; }

.synth-source-anchor { font-size: 0.7rem; color: #6e7681; margin-top: 4px; }
.published-pill { background: #1c2a1f; color: #7ee787; border: 1px solid #2ea043; }
.published-stale { color: #d29922; }
</style>
